import { createFileRoute, Link } from "@tanstack/react-router";
import { ServiceDetail } from "@/components/ServiceDetail";
import { faqPageSchema, type Faq } from "@/data/serviceFaqs";
import hero from "@/assets/smart-home.webp";
import apexTheaterRichImg from "@/assets/Apex_Theater.webp.asset.json";

const URL = "https://apexav.com/denver/smart-home-installation";

const NEARBY = [
  { name: "Cherry Creek", to: "/cherry-creek/smart-home-installation" as const },
  { name: "Highlands Ranch", to: "/highlands-ranch/smart-home-installation" as const },
  { name: "Castle Rock", to: "/castle-rock/smart-home-installation" as const },
  { name: "Castle Pines", to: "/castle-pines/smart-home-installation" as const },
];

const denverSmartFaqs: Faq[] = [
          {
            q: "How much does smart home installation cost in Denver?",
            a: "A basic Denver smart home starter — a whole-home automation hub, one keypad, whole-home audio for 4 zones, and lighting on the main level — starts around $15,000 installed. Whole-home automation with professional smart lighting, motorized shades, theater integration, cameras, and voice control typically runs $40,000–$150,000. Luxury Cherry Creek and Castle Pines estates with fully custom control scopes can exceed $250,000. Every Denver project is fixed-bid.",
          },
          {
            q: "Which smart home platform is best for a Denver home?",
            a: "For most Denver luxury homes we specify a leading whole-home automation platform (best value + broadest integrations), a premium iOS-native platform for the best mobile UX, or a privacy-first natural-language voice platform. Ultra-custom control systems are our top pick for the largest estates and commercial-adjacent installations. All integrate with professional lighting and shading — the right choice depends on your budget, existing gear, and how you actually live in the home. Our free Denver consultation compares them side by side.",
          },
          {
            q: "Do you install smart lighting and motorized shades too?",
            a: "Yes — Apex AV is a certified professional lighting and shading installer for Denver. We handle wired and wireless smart lighting, tunable-white lighting, and motorized shades from all major manufacturers. Lighting, shades, and automation are typically designed together so scenes trigger everything at once.",
          },
          {
            q: "Will you work with my Denver builder, architect, or interior designer?",
            a: "Regularly. We partner with builders and designers across the Denver Metro — Cherry Creek, Polo Club, Castle Pines Village, BackCountry, and Colorado Golf Club. We coordinate prewire, keypad placement, rack location, and wire management during framing so nothing shows up as an afterthought.",
          },
          {
            q: "Which Denver neighborhoods do you serve?",
            a: "The entire Denver Metro and Front Range — Cherry Creek, Hilltop, Polo Club, Country Club, Belcaro, Greenwood Village, Cherry Hills Village, Highlands Ranch, Castle Pines, Castle Rock, Lone Tree, Parker, Littleton, Centennial, Lakewood, Golden, Boulder, and Evergreen.",
          },
          {
            q: "Do you offer service after install?",
            a: "Yes. Every Denver Apex AV client gets a lifetime local support relationship — remote diagnostics on the whole system, priority in-person visits, and free reprogramming for the first 12 months. Denver-based, not offshored.",
          },
];

export const Route = createFileRoute("/denver/smart-home-installation")({
  head: () => ({
    meta: [
      { title: "Smart Home Installation Denver, CO | Apex AV" },
      { name: "description", content: "Denver's trusted smart home installers. Whole-home automation, smart lighting, and voice control across Denver Metro. Free consultation." },
      { name: "keywords", content: "smart home installation Denver, Denver smart home installer, home automation Denver, luxury smart home Denver, smart lighting Denver" },
      { property: "og:title", content: "Smart Home Installation Denver — Apex AV" },
      { property: "og:description", content: "Custom smart home design & install for Denver luxury homes." },
      { property: "og:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
      { property: "og:image:alt", content: "Apex AV custom home theater installation in Denver, Colorado" },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Smart Home Installation Denver — Apex AV" },
      { name: "twitter:description", content: "Denver's trusted whole-home automation and smart home installer." },
      { name: "twitter:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "preload", as: "image", href: hero, fetchPriority: "high" } as never,
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            faqPageSchema(denverSmartFaqs, URL),
            {
              "@type": ["LocalBusiness", "HomeAndConstructionBusiness"],
              "@id": `${URL}#localbusiness`,
              name: "Apex AV — Smart Home Installation in Denver",
              image: `https://apexav.com${apexTheaterRichImg.url}`,
              logo: "https://apexav.com/logo.png",
              url: URL,
              telephone: "+1-303-317-3233",
              email: "info@apexav.com",
              priceRange: "$$$",
              address: {
                "@type": "PostalAddress",
                streetAddress: "2921 W 38th AVE STE 169",
                addressLocality: "Denver",
                addressRegion: "CO",
                postalCode: "80211",
                addressCountry: "US",
              },
              geo: { "@type": "GeoCoordinates", latitude: 39.7714, longitude: -105.0234 },
              areaServed: [
                { "@type": "City", name: "Denver, CO" },
                { "@type": "AdministrativeArea", name: "Denver Metro & Front Range, Colorado" },
              ],
              openingHoursSpecification: [
                { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "08:00", closes: "18:00" },
                { "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: "09:00", closes: "16:00" },
              ],
              sameAs: [
                "https://share.google/jMyCOceBfsaHfeVQp",
                "https://x.com/ApexAV80211",
                "https://www.facebook.com/apexavdenver",
              ],
              parentOrganization: { "@id": "https://apexav.com/#organization" },
              makesOffer: {
                "@type": "Offer",
                priceCurrency: "USD",
                price: 15000,
                itemOffered: { "@type": "Service", name: "Smart Home Installation in Denver" },
              },
            },
            {
              "@type": "Service",
              name: "Smart Home Installation in Denver, CO",
              description: "Custom smart home design and installation across Denver, Colorado.",
              url: URL,
              provider: { "@id": "https://apexav.com/#organization" },
              areaServed: { "@type": "City", name: "Denver, CO" },
              serviceType: "Smart Home Installation",
              offers: { "@type": "Offer", priceCurrency: "USD", price: "15000" },
            },
          ],
        }),
      },
    ],
  }),
  component: DenverSmartHome,
});

function DenverSmartHome() {
  return (
    <>
      <ServiceDetail
        topics={["smart-home", "lighting", "shading", "networking"]}
        eyebrow="Smart home installation Denver"
        title="Smart Home Installation in Denver"
        intro={<>Custom <strong>smart home installation in Denver, CO</strong> — <strong>whole-home automation, smart lighting, and voice control</strong> design, install, and lifetime support for Denver luxury homes. One app for lights, shades, theater, climate, security, and audio. Free in-home consultation from a local Denver team, fixed-bid pricing before install.</>}
        image={hero}
        imageAlt="Smart home installation in Denver, Colorado — smart keypad and lighting in a luxury Denver residence"
        startingPrice="$15,000"
        priceNote="Most Denver whole-home automation projects run $15K–$150K depending on scope."
        bullets={[
          "Certified whole-home automation dealer serving Denver",
          "Professional smart lighting design (wired & wireless)",
          "Whole-home audio (Sonos, Sonance) and video distribution",
          "Motorized shading integrated with lighting & climate",
          "Security, cameras, doorbells, and access control",
          "Enterprise-grade WiFi and network engineering",
          "Free in-home Denver consultation & fixed-bid quote",
          "Lifetime local support — Denver-based team",
        ]}
        features={[
          { title: "One app, one remote", desc: "Every subsystem — lights, shades, AV, HVAC, security — on the same Denver-tuned interface." },
          { title: "Denver climate scenes", desc: "Automation that reacts to Front Range sun, snow load, and afternoon storms." },
          { title: "Local support", desc: "Same Denver crew designs, installs, and calibrates. No offshored help desk." },
        ]}
        faqs={denverSmartFaqs}
      />
      <section className="container mx-auto px-6 py-16 border-t border-border">
        <h2 className="font-display text-2xl md:text-3xl font-bold">
          Denver smart home installation by neighborhood
        </h2>
        <p className="mt-2 text-muted-foreground max-w-2xl">
          HOA rules, construction era, and wiring realities vary by neighborhood. Pick yours for a page tuned to it.
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {NEARBY.map((n) => (
            <Link
              key={n.name}
              to={n.to}
              className="rounded-xl border border-border bg-card p-5 shadow-card hover:border-brand/50 transition"
            >
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Smart home</div>
              <div className="mt-2 font-display text-lg font-semibold">{n.name}</div>
              <div className="mt-3 text-sm text-brand-text">View page →</div>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}
